import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { createBookingSchema, type CreateBookingInput } from '@hch/shared';
import { useCreateBooking, messageOf } from '~/lib/queries';
import { useAuth } from '~/lib/auth';
import { useEtatPersistant } from '~/lib/use-etat-persistant';
import { STEPS, type BookingDraft, type Step } from './booking-types';
import { StepIndicator } from './step-shell';
import { AddressStep } from './steps/address-step';
import { BicycleStep } from './steps/bicycle-step';
import { OperationStep } from './steps/operation-step';
import { PackageStep } from './steps/package-step';
import { SlotStep } from './steps/slot-step';
import { ExtrasStep } from './steps/extras-step';
import { SummaryStep } from './steps/summary-step';

/**
 * Tunnel de reservation — parcours principal de l'application.
 *
 * L'ordre des etapes suit litteralement l'enonce : adresse, cycle, operation de
 * maintenance, forfait, creneau, complements. Deux partis pris :
 *
 *   - La couverture est verifiee DES l'etape 1. Un client hors zone l'apprend
 *     immediatement, avant d'avoir saisi quoi que ce soit d'autre — plutot qu'apres
 *     cinq etapes, ce qui serait une perte de temps et une mauvaise experience.
 *
 *   - Le compte n'est jamais un prealable (RG-04). Un visiteur connecte se voit
 *     simplement proposer ses adresses et ses cycles enregistres.
 */

export function BookingPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const createBooking = useCreateBooking();

  // Etape et brouillon survivent a un rechargement : sept etapes perdues sur une
  // touche F5, c'est un client perdu. Voir `useEtatPersistant` pour le detail des
  // choix — session plutot que local, peremption, et ce qui n'y est jamais ecrit.
  const [step, setStep, oublierEtape] = useEtatPersistant<Step>('hch-reservation-etape', 'adresse');
  const [draft, setDraft, oublierBrouillon] = useEtatPersistant<BookingDraft>(
    'hch-reservation-brouillon',
    {
      products: [],
      mediaKeys: [],
      clientNotes: '',
      guest: { email: '', firstName: '', lastName: '', phone: '' },
    },
  );
  const [acceptTerms, setAcceptTerms] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const patch = (values: Partial<BookingDraft>) =>
    setDraft((previous) => ({ ...previous, ...values }));

  const coords = draft.newAddress ? { lat: draft.newAddress.lat, lng: draft.newAddress.lng } : null;

  const stepIndex = STEPS.findIndex((item) => item.id === step);

  function goTo(next: Step) {
    setErrors({});
    setStep(next);
    // Le retour en haut est indispensable sur mobile : sans lui, l'utilisateur
    // arrive au milieu de l'etape suivante.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submit() {
    setErrors({});

    const payload = {
      ...(isAuthenticated ? {} : { guest: draft.guest }),
      ...(draft.addressId ? { addressId: draft.addressId } : { newAddress: draft.newAddress }),
      ...(draft.bicycleId ? { bicycleId: draft.bicycleId } : { newBicycle: draft.newBicycle }),
      packageId: draft.packageId,
      slotIds: draft.slotIds,
      products: draft.products,
      mediaKeys: draft.mediaKeys,
      clientNotes: draft.clientNotes || null,
      acceptTerms,
    };

    const parsed = createBookingSchema.safeParse(payload);
    if (!parsed.success) {
      const fieldErrors = Object.fromEntries(
        parsed.error.issues.map((issue) => [issue.path.join('.') || '_', issue.message]),
      );
      setErrors(fieldErrors);
      toast.error(Object.values(fieldErrors)[0] ?? 'Le formulaire est incomplet.');
      return;
    }

    try {
      const result = await createBooking.mutateAsync(parsed.data as CreateBookingInput);

      // La reservation est enregistree : le brouillon n'a plus de raison d'etre. Le
      // laisser en place rouvrirait le tunnel pre-rempli sur un creneau desormais pris.
      oublierBrouillon();
      oublierEtape();

      if (result.guestClaimToken) {
        // RG-04 : le jeton est transmis a l'inscription pour convertir le compte
        // invite sans creer de doublon.
        const params = new URLSearchParams({
          claim: result.guestClaimToken,
          ref: result.reference,
          email: draft.guest.email,
          prenom: draft.guest.firstName,
          nom: draft.guest.lastName,
          tel: draft.guest.phone,
        });
        navigate(`/inscription?${params.toString()}`, { replace: true });
      } else {
        toast.success(`Rendez-vous confirme — ${result.reference}`);
        navigate('/mon-compte', { replace: true });
      }
    } catch {
      // Le message d'erreur est deja affiche par la mutation. Sur collision de
      // creneau, on ramene l'utilisateur au choix d'horaire : c'est la seule
      // action utile qu'il puisse entreprendre.
      if (createBooking.error && messageOf(createBooking.error).includes('creneau')) {
        goTo('creneau');
      }
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8">
      <StepIndicator current={stepIndex} />

      <div className="mt-6">
        {step === 'adresse' ? (
          <AddressStep draft={draft} patch={patch} onNext={() => goTo('cycle')} />
        ) : null}

        {step === 'cycle' ? (
          <BicycleStep
            draft={draft}
            patch={patch}
            onBack={() => goTo('adresse')}
            onNext={() => goTo('operation')}
          />
        ) : null}

        {step === 'operation' ? (
          <OperationStep
            draft={draft}
            patch={patch}
            onBack={() => goTo('cycle')}
            onNext={() => goTo('forfait')}
          />
        ) : null}

        {step === 'forfait' ? (
          <PackageStep
            draft={draft}
            patch={patch}
            onBack={() => goTo('operation')}
            onNext={() => goTo('creneau')}
          />
        ) : null}

        {step === 'creneau' ? (
          <SlotStep
            draft={draft}
            coords={coords}
            patch={patch}
            onBack={() => goTo('forfait')}
            onNext={() => goTo('complements')}
          />
        ) : null}

        {step === 'complements' ? (
          <ExtrasStep
            draft={draft}
            patch={patch}
            onBack={() => goTo('creneau')}
            onNext={() => goTo('recapitulatif')}
          />
        ) : null}

        {step === 'recapitulatif' ? (
          <SummaryStep
            draft={draft}
            patch={patch}
            errors={errors}
            acceptTerms={acceptTerms}
            setAcceptTerms={setAcceptTerms}
            isAuthenticated={isAuthenticated}
            userName={user ? `${user.firstName} ${user.lastName}` : undefined}
            submitting={createBooking.isPending}
            onBack={() => goTo('complements')}
            onSubmit={submit}
          />
        ) : null}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Fil d'etapes
// ═══════════════════════════════════════════════════════════════════════════════
